import './PrimaryLayout.css'

import { useEffect, useState } from 'react'
import { Link, Outlet, NavLink, useNavigate } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import { agentChat, session } from '../store'

import rheyaAvatar from '../assets/rheya-avatar.jpg'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { usePhotoObjectUrl } from '../hooks/usePhotoObjectUrl'
import { BrandLogo } from '../components/BrandLogo'
import { ChatModal } from '../components/ChatModal'
import { ChatMinimized } from '../components/ChatMinimized'
import { unlockAudioPlayback } from '../lib/voice'

const rheyaBadgePollMs = 60_000

export function PrimaryLayout() {
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const currentSession = useAtomValue(session.sessionAtom)
  const logout = useSetAtom(session.logoutAtom)
  const updateSession = useSetAtom(session.updateSessionAtom)
  const navigate = useNavigate()
  const [breadcrumbs] = useBreadcrumbs()
  const chatIsOpen = useAtomValue(agentChat.agentChatIsOpenAtom)
  const chatIsMinimized = useAtomValue(agentChat.agentChatIsMinimizedAtom)
  const chatInitialContext = useAtomValue(agentChat.agentChatInitialContextAtom)
  const openChat = useSetAtom(agentChat.agentChatOpenAtom)
  const closeChat = useSetAtom(agentChat.agentChatCloseAtom)
  const minimizeChat = useSetAtom(agentChat.agentChatMinimizeAtom)
  const restoreChat = useSetAtom(agentChat.agentChatRestoreAtom)

  const rheyaHasMessage = Boolean(currentSession?.rheyaHasMessage)

  // Refresh badge when chat closes (member may have replied) and on a slow poll
  // so scheduled check-ins appear without a full page reload.
  useEffect(() => {
    if (chatIsOpen) return
    updateSession()
    const intervalId = window.setInterval(() => updateSession(), rheyaBadgePollMs)
    return () => window.clearInterval(intervalId)
  }, [chatIsOpen, updateSession])

  const handleLogout = async () => {
    setUserMenuOpen(false)
    await logout()
    navigate('/login')
  }

  const userEmail = currentSession?.user.email ?? ''
  const userInitial = userEmail.charAt(0).toUpperCase()
  const userPhotoUrl = usePhotoObjectUrl(currentSession?.user.profile?.mainPhotoId, 100)

  return (
    <div className="primary-layout">
      <aside className="primary-layout__sidebar">
        <div className="primary-layout__sidebar-top">
          <div className="primary-layout__branding">
            <BrandLogo to="/profile" layout="full" size="md" onChrome />
            <p className="primary-layout__tagline">Curated matches. Genuine intentions.</p>
          </div>

          <nav className="primary-layout__nav" aria-label="Main">
            <NavLink
              to="/profile"
              className={({ isActive }) =>
                `primary-layout__nav-link${isActive ? ' primary-layout__nav-link--active' : ''}`
              }
            >
              Profile
            </NavLink>
            <NavLink
              to="/matches"
              className={({ isActive }) =>
                `primary-layout__nav-link${isActive ? ' primary-layout__nav-link--active' : ''}`
              }
            >
              Matches
            </NavLink>
          </nav>
        </div>

        <footer className="primary-layout__sidebar-footer">
          <nav className="primary-layout__legal" aria-label="Legal">
            <a href="https://revel.blog" className="primary-layout__legal-link">
              Blog
            </a>
            <Link to="/terms" className="primary-layout__legal-link">
              Terms
            </Link>
            <Link to="/privacy" className="primary-layout__legal-link">
              Privacy
            </Link>
          </nav>
          <p className="primary-layout__copyright">&copy; {new Date().getFullYear()} Revel Club</p>
        </footer>
      </aside>

      <div className="primary-layout__body">
        <header className="primary-layout__header">
          <BrandLogo
            to="/profile"
            layout="full"
            size="sm"
            className="primary-layout__mobile-logo"
          />

          {breadcrumbs.length > 0 && (
            <nav className="primary-layout__breadcrumbs" aria-label="Breadcrumbs">
              {breadcrumbs.map((crumb, index) => (
                <span key={`${crumb.path}:${crumb.label}`}>
                  {index > 0 && <span className="primary-layout__breadcrumb-separator">›</span>}
                  {index === breadcrumbs.length - 1 ? (
                    <span className="primary-layout__breadcrumb primary-layout__breadcrumb--active">
                      {crumb.label}
                    </span>
                  ) : crumb.path ? (
                    <Link
                      to={crumb.path}
                      state={crumb.state}
                      className="primary-layout__breadcrumb"
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="primary-layout__breadcrumb">{crumb.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}

          <div className="primary-layout__user-menu">
            <button
              type="button"
              className="primary-layout__user-avatar"
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              {userPhotoUrl ? (
                <img
                  src={userPhotoUrl}
                  alt={userEmail}
                  className="primary-layout__user-avatar-image"
                />
              ) : (
                userInitial
              )}
            </button>
            {userMenuOpen && (
              <div className="primary-layout__user-dropdown">
                <div className="primary-layout__user-info">
                  <span className="primary-layout__user-email">{userEmail}</span>
                </div>
                <div className="primary-layout__user-dropdown-divider" />
                <Link
                  to="/settings"
                  className="primary-layout__user-dropdown-item"
                  onClick={() => setUserMenuOpen(false)}
                >
                  Settings
                </Link>
                <button
                  type="button"
                  className="primary-layout__user-dropdown-item"
                  onClick={handleLogout}
                >
                  Log out
                </button>
              </div>
            )}
          </div>
        </header>

        <div className="primary-layout__main">
          <Outlet />
        </div>

        <nav className="primary-layout__bottom-nav" aria-label="Mobile">
          <NavLink
            to="/profile"
            className={({ isActive }) =>
              `primary-layout__bottom-nav-link${isActive ? ' primary-layout__bottom-nav-link--active' : ''}`
            }
          >
            Profile
          </NavLink>
          <NavLink
            to="/matches"
            className={({ isActive }) =>
              `primary-layout__bottom-nav-link${isActive ? ' primary-layout__bottom-nav-link--active' : ''}`
            }
          >
            Matches
          </NavLink>
        </nav>

        <button
          type="button"
          className={`primary-layout__agent${rheyaHasMessage ? ' primary-layout__agent--has-message' : ''}`}
          title={rheyaHasMessage ? 'Rheya left you a message' : 'Chat with Rheya'}
          aria-label={rheyaHasMessage ? 'Chat with Rheya, new message' : 'Chat with Rheya'}
          onClick={() => {
            // Sync unlock on gesture — do not await (Chrome user-gesture token).
            unlockAudioPlayback()
            // Session discriminator: open unmatch interview when pending, not general.
            if (currentSession?.rheyaPendingSource === 'unmatch') {
              openChat({ mode: 'unmatch' })
            } else {
              openChat({ mode: 'general' })
            }
          }}
        >
          <img src={rheyaAvatar} alt="" className="primary-layout__agent-avatar" />
          {rheyaHasMessage && <span className="primary-layout__agent-badge" aria-hidden="true" />}
        </button>

        {chatIsOpen && !chatIsMinimized && (
          <ChatModal
            initialContext={chatInitialContext}
            onClose={closeChat}
            onMinimize={minimizeChat}
          />
        )}
        {chatIsOpen && chatIsMinimized && (
          <ChatMinimized
            onRestore={() => {
              unlockAudioPlayback()
              restoreChat()
            }}
            onClose={closeChat}
          />
        )}
      </div>
    </div>
  )
}
